/**
 * This custom hook provides a flexible functionality to sign-in via a social auth provider.
 * The caller only has to provide the provider as argument and this hook will take care about
 * the login via popup as well as the initialization of the roles (if not yet set).
 */

import {useContext, useState} from "react";
import {firebaseService} from "../../firebase/Firebase";
import {FirebaseAuthContext} from "../../firebase/FirebaseAuthProvider";
import {useIntl} from "react-intl";
import {useSnackbar} from "notistack";

const useLinkingSignIn = (authProvider) => {
    // Retrieve get parameter from the Moodle request including the generated Firebase custom token
    const searchParams = new URLSearchParams(window.location.search)

    const moodleUid = searchParams.get('moodleUid')
    const commonEmail = searchParams.get('commonEmail')
    const existingAuthProvider = searchParams.get('existingAuthProvider')
    const ltiKey = searchParams.get('ltik')

    const [linkingSuccess, setLinkingSuccess] = useState(false)

    const {setSignedInUserMoodleLtiKey} = useContext(FirebaseAuthContext)

    const {enqueueSnackbar} = useSnackbar();

    // Internationalization for simple strings
    const intl = useIntl();

    /*
    In order to link the accounts, the user has to confirm the already existing account (via login).
    The user will then be logged-in via the already existing account after the linking.
    Future logins through Moodle will then be done through custom token login automatically (i.e. no linking step required).
     */
    const linkBySigningInWithGivenProvider = () => {

        /*
        Set the LTI key we get from Moodle in the FirebaseAuthContext, so that we can use it
        throughout the application. This key is not important for the actual REG session, but
        it becomes necessary as soon as a communication with Moodle is required.
        IMPORTANT: The context's lti key (in FirebaseAuthContext) cannot be used in this function,
        as it is not updated immediately, but in successive components it is accessible.
         */
        setSignedInUserMoodleLtiKey(ltiKey)

        authProvider.addScope('email');

        return firebaseService.auth().signInWithPopup(authProvider)
            .then((res) => {
                /*
                This action will trigger the "FirebaseAuthProvider" to call the onAuthStateChanged method.
                But in addition the roles have to be set (see next handler)
                 */
                return res.user.getIdToken()
            })
            .then((idToken) => {
                // Do linking on backend (important for future calls from Moodle)
                return fetch('/reg-main/profile/linkAccount', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(
                        {
                            idToken: idToken,
                            moodleUid: moodleUid,
                            commonEmail: commonEmail
                        })
                })
            })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                setLinkingSuccess(true)
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.signIn.messages.linking.methodSocial.success',
                        defaultMessage: 'Accounts successfully linked.'
                    }), {
                        variant: 'success',
                        preventDuplicate: true,
                        autoHideDuration: 3000
                    });

            })
            .catch(async (error) => {
                // Most common error - linking with a wrong email address
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.signIn.messages.linking.methodSocial.error',
                        defaultMessage: 'Something went wrong during the account linking: {error}.'
                    }, {email: commonEmail, error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
    }

    const linkBySigningInWithEmailAndPassword = (authEmail, authPassword) => {
        return firebaseService.auth().signInWithEmailAndPassword(authEmail, authPassword)
            .then((res) => {
                /*
                This action will trigger the "FirebaseAuthProvider" to call the onAuthStateChanged method.
                But in addition the roles have to be set (see next handler)
                 */
                return res.user.getIdToken()
            })
            .then((idToken) => {
                // Do linking on backend (important for future calls from Moodle)
                return fetch('/reg-main/profile/linkAccount', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(
                        {
                            idToken: idToken,
                            moodleUid: moodleUid,
                            commonEmail: commonEmail
                        })
                })
            })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }
                setLinkingSuccess(true)
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.signIn.messages.linking.methodEmail.success',
                        defaultMessage: 'Accounts successfully linked.'
                    }), {
                        variant: 'success',
                        preventDuplicate: true,
                        autoHideDuration: 3000
                    });
            })
            .catch((error) => {
                // Most common error - linking with a wrong email address
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.signIn.messages.linking.methodEmail.error',
                        defaultMessage: 'Something went wrong during the account linking: {error}.'
                    }, {email: commonEmail, error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
    }

    return {
        linkBySigningInWithGivenProvider,
        linkBySigningInWithEmailAndPassword,
        linkingSuccess,
        commonEmail,
        existingAuthProvider
    }
}
export default useLinkingSignIn;